import React, { useState, useContext, useRef, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { Copy, MessageCircleMore, Mic, PenSquare, Pencil, RotateCcw, Sparkles, SendHorizontal, Square, ThumbsDown, ThumbsUp } from "lucide-react";
import { UsuarioContext } from "../../../providers/userContext";
import {
  preguntarAsistenteStream, transcribirAudio, listarConversaciones, obtenerConversacion,
  renombrarConversacion, eliminarConversacion, obtenerLimiteAsistente, enviarFeedback,
} from "../../../services/asistente.service";
import LAEAIGrabador from "./LAEAIGrabador";
import LAEAIHistorial from "./LAEAIHistorial";
import LAEAIRespuesta from "./LAEAIRespuesta";
import LAEAIDerivacion from "./LAEAIDerivacion";
import LAEAITarjetas from "./LAEAITarjetas";
import LAEAILimite from "./LAEAILimite";
import { puedeUsarWidgetChatElite } from "../../../utilities/chatEliteUsuario";
import { grabacionSoportada } from "../../../hooks/useGrabadorAudio";

// Se calcula una vez: el navegador permite grabar audio (micrófono + MediaRecorder + Web Audio)
const audioSoportado = grabacionSoportada();

/**
 * "Tu IA Normativa" nueva (asistente propio, back: /api/asistente).
 * - Historial de conversaciones guardado en el servidor (izquierda; en móvil, cajón). La conversación abierta va en
 *   la URL (?c=id) para que sobreviva a recargar la página.
 * - La respuesta llega en streaming (SSE) y se va pintando; se puede detener. Preguntas escritas o por voz.
 */
const MENSAJE_ERROR = {
  ASISTENTE_DEMASIADAS_PETICIONES: "laeai.errors.tooMany",
  ASISTENTE_TIMEOUT: "laeai.errors.timeout",
  ASISTENTE_NO_CONFIGURADO: "laeai.errors.unavailable",
  ASISTENTE_ROL_NO_SOPORTADO: "laeai.errors.unavailable",
  PREGUNTA_DEMASIADO_LARGA: "laeai.errors.tooLong",
  CONVERSACION_LLENA: "laeai.errors.full",
  CONVERSACION_NO_ENCONTRADA: "laeai.errors.notFound",
  AUDIO_DEMASIADO_CORTO: "laeai.audio.tooShort",
};
const textoError = (t, codigo) => t(MENSAJE_ERROR[codigo] || "laeai.errors.generic");

// Mensaje del servidor -> mensaje de la pantalla
const desdeServidor = (m) => ({
  id: m.id,
  role: m.rol === "usuario" ? "user" : "assistant",
  content: m.texto || "",
  estado: m.estado,
  audio: !!m.audio,
  error: m.error,
  derivacion: m.derivacion || null,
  tarjetas: m.tarjetas || null,
  util: typeof m.feedback?.util === "boolean" ? m.feedback.util : null,
});

function LAEAI() {
  const { t } = useTranslation("common");
  const user = useContext(UsuarioContext);
  const [parametros, setParametros] = useSearchParams();
  const idEnUrl = Number(parametros.get("c")) || null;

  const [conversaciones, setConversaciones] = useState([]);
  const [cargandoLista, setCargandoLista] = useState(true);
  const [errorLista, setErrorLista] = useState(false);
  const [activaId, setActivaId] = useState(idEnUrl);
  const [mensajes, setMensajes] = useState([]);
  const [cargandoConversacion, setCargandoConversacion] = useState(false);
  const [historialMovil, setHistorialMovil] = useState(false);
  // Respuesta que está llegando: { texto, herramienta, transcribiendo }
  const [enCurso, setEnCurso] = useState(null);
  const [input, setInput] = useState("");
  const [inputFocused, setInputFocused] = useState(false);
  const [fraseIdx, setFraseIdx] = useState(0);
  const messagesEndRef = useRef(null);
  // Scroll automático solo si el usuario está abajo: si sube mientras responde la IA, se queda donde está
  const contenedorRef = useRef(null);
  const pegadoAbajo = useRef(true);
  const ultimoScroll = useRef(0);
  const alHacerScroll = () => {
    const el = contenedorRef.current;
    if (!el) return;
    const distancia = el.scrollHeight - el.scrollTop - el.clientHeight;
    // Abajo del todo: vuelve a seguir la respuesta. Hacia arriba: lo ha movido el usuario (el automático solo baja)
    if (distancia < 80) pegadoAbajo.current = true;
    else if (el.scrollTop < ultimoScroll.current) pegadoAbajo.current = false;
    ultimoScroll.current = el.scrollTop;
  };
  const montado = useRef(true);
  const controlador = useRef(null);
  const nombre = (user.nombreContacto || user.nombre)?.split(" ")[0] || "";
  const frasesCarga = t("laeai.loadingPhrases", { returnObjects: true });
  const cargando = enCurso !== null;

  useEffect(() => {
    montado.current = true;
    // Mismo ajuste de layout que LegalIA: el chat ocupa la altura disponible sin scroll de página
    const contenedor = document.querySelector(".contenedor");
    if (contenedor) {
      contenedor.style.overflow = "hidden";
      contenedor.style.paddingBottom = "0";
      contenedor.style.minHeight = "unset";
      contenedor.style.height = "calc(100vh - 56px)";
    }
    return () => {
      // Si sale de la pantalla, la respuesta sigue llegando y el back la guarda en el historial
      montado.current = false;
      if (contenedor) {
        contenedor.style.overflow = "";
        contenedor.style.paddingBottom = "";
        contenedor.style.minHeight = "";
        contenedor.style.height = "";
      }
    };
  }, []);

  // Historial (se recarga si cambia el usuario: entrar como oficina / volver a mi cuenta)
  const cargarLista = useCallback(async () => {
    setCargandoLista(true);
    try {
      const lista = await listarConversaciones();
      if (montado.current) {
        setConversaciones(lista);
        setErrorLista(false);
      }
    } catch {
      if (montado.current) setErrorLista(true);
    } finally {
      if (montado.current) setCargandoLista(false);
    }
  }, []);
  useEffect(() => { cargarLista(); }, [cargarLista, user?.id]);

  // Límite diario de consultas a la IA (compartido con el Chat Élite): agotado = chat bloqueado hasta medianoche
  const [limite, setLimite] = useState(null);
  const cargarLimite = useCallback(() => {
    obtenerLimiteAsistente().then((l) => { if (montado.current) setLimite(l); }).catch(() => {});
  }, []);
  useEffect(() => { cargarLimite(); }, [cargarLimite, user?.id]);
  const bloqueado = !!limite?.agotado;

  // Abrir la conversación de la URL (al entrar, al recargar o con atrás/adelante del navegador)
  useEffect(() => {
    if (cargando) return;
    if (!idEnUrl) {
      setActivaId(null);
      setMensajes([]);
      return;
    }
    if (idEnUrl === activaId && mensajes.length) return;
    let cancelado = false;
    setActivaId(idEnUrl);
    pegadoAbajo.current = true;
    setCargandoConversacion(true);
    obtenerConversacion(idEnUrl)
      .then((c) => { if (!cancelado) setMensajes(c.mensajes.map(desdeServidor)); })
      .catch((err) => {
        if (cancelado) return;
        toast.error(err.codigo === "CONVERSACION_NO_ENCONTRADA" ? t("laeai.errors.notFound") : t("laeai.history.openError"));
        setParametros({}, { replace: true });
      })
      .finally(() => { if (!cancelado) setCargandoConversacion(false); });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idEnUrl]);

  useEffect(() => {
    if (!pegadoAbajo.current) return;
    if (mensajes.length > 0 || cargando) messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [mensajes, enCurso?.texto, cargando]);

  useEffect(() => {
    if (!cargando || !Array.isArray(frasesCarga) || frasesCarga.length === 0) return;
    setFraseIdx(0);
    const intervalo = setInterval(() => setFraseIdx((i) => (i + 1) % frasesCarga.length), 2500);
    return () => clearInterval(intervalo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargando, frasesCarga.length]);

  const vacia = mensajes.length === 0 && !cargando && !cargandoConversacion;
  const sugerencias = useMemo(() => {
    const lista = t("laeai.suggestedQuestions", { returnObjects: true });
    const arr = Array.isArray(lista) ? [...lista] : [];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr.slice(0, 4);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vacia, t]);

  const anadirMensaje = (mensaje) => {
    if (montado.current) setMensajes((actuales) => [...actuales, mensaje]);
  };

  const actualizarEnCurso = (cambio) => {
    if (montado.current) setEnCurso((actual) => (actual ? { ...actual, ...cambio } : actual));
  };

  // Pulgar arriba/abajo sobre una respuesta: se marca al momento y se deshace si el servidor no lo guarda
  const votar = async (mensajeId, util) => {
    const anterior = mensajes.find((m) => m.id === mensajeId)?.util ?? null;
    if (anterior === util || !activaId) return;
    const marcar = (valor) => setMensajes((lista) => lista.map((m) => (m.id === mensajeId ? { ...m, util: valor } : m)));
    marcar(util);
    try {
      await enviarFeedback(activaId, mensajeId, util);
      // toast.success(t("laeai.feedback.thanks"));
    } catch {
      if (!montado.current) return;
      marcar(anterior);
      toast.error(t("laeai.feedback.error"));
    }
  };

  const copiar = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
      // toast.success(t("laeai.copied"));
    } catch {
      toast.error(t("laeai.copyError"));
    }
  };

  // Sube la conversación al principio del historial (o la añade si es nueva)
  const tocarConversacion = (id, titulo) => {
    setConversaciones((lista) => {
      const existente = lista.find((c) => c.id === id);
      const actualizada = { ...(existente || { id, creada_at: new Date().toISOString() }), titulo: titulo || existente?.titulo, ultimo_mensaje_at: new Date().toISOString() };
      return [actualizada, ...lista.filter((c) => c.id !== id)];
    });
  };

  // Envía una pregunta (escrita o ya transcrita de un audio) y recibe la respuesta en streaming.
  // reemplazar: rehace el último turno (pregunta editada o volver a generar): se quita de la pantalla y del historial
  const procesarPregunta = async (pregunta, { audio = false, reemplazar = false } = {}) => {
    if (reemplazar && montado.current) {
      setMensajes((actuales) => {
        const ultima = actuales.map((m) => m.role).lastIndexOf("user");
        return ultima >= 0 ? actuales.slice(0, ultima) : actuales;
      });
    }
    // Al preguntar, siempre se baja a la nueva pregunta y se sigue la respuesta
    pegadoAbajo.current = true;
    anadirMensaje({ role: "user", content: pregunta, audio });
    setEnCurso({ texto: "", herramienta: null });
    controlador.current = new AbortController();
    let texto = "";
    let derivacion = null;
    let tarjetas = [];
    let conversacionId = activaId;
    // Título final (el de la IA, si ya está: al empezar llega el automático)
    let tituloFinal;
    try {
      const data = await preguntarAsistenteStream(pregunta, {
        conversacionId: activaId,
        audio,
        // Sin conversación todavía (falló antes de crearse) no hay nada que sustituir en el servidor
        reemplazarUltima: reemplazar && !!activaId,
        signal: controlador.current.signal,
        onConversacion: (c) => {
          conversacionId = c.id;
          if (!montado.current) return;
          tocarConversacion(c.id, c.titulo);
          if (c.nueva) {
            setActivaId(c.id);
            setParametros({ c: String(c.id) }, { replace: true });
          }
        },
        onTexto: (trozo) => {
          texto += trozo;
          actualizarEnCurso({ texto, herramienta: null });
        },
        onHerramienta: (herramienta) => actualizarEnCurso({ herramienta }),
        onDerivacion: (d) => { derivacion = d; },
        onTarjeta: (tarjeta) => {
          tarjetas = [...tarjetas, tarjeta];
          actualizarEnCurso({ tarjetas });
        },
      });
      anadirMensaje({
        id: data.mensaje_id, role: "assistant", content: data.respuesta, estado: data.completa ? "ok" : "error",
        derivacion: data.derivacion || derivacion,
        tarjetas: data.tarjetas || tarjetas,
      });
      if (data.limite && montado.current) setLimite(data.limite);
      tituloFinal = data.titulo;
    } catch (err) {
      // Límite diario agotado: no se ha enviado nada. Se deshace en pantalla y se bloquea el chat con la cuenta atrás
      if (err.codigo === "LIMITE_DIARIO") {
        if (!montado.current) return;
        setLimite(err.detalle || { ...(limite || {}), agotado: true, restantes: 0 });
        if (reemplazar && activaId) {
          obtenerConversacion(activaId).then((c) => { if (montado.current) setMensajes(c.mensajes.map(desdeServidor)); }).catch(() => {});
        } else {
          setMensajes((actuales) => actuales.slice(0, -1));
          if (!audio) setInput(pregunta);
        }
        return;
      }
      // Lo que ya había llegado se queda (el back lo guarda igual como respuesta parcial)
      if (texto) anadirMensaje({ role: "assistant", content: texto, estado: "parcial", error: err.codigo, derivacion, tarjetas });
      else if (err.codigo !== "ASISTENTE_CANCELADO") anadirMensaje({ role: "assistant", content: "", estado: "error", error: err.codigo });
    } finally {
      controlador.current = null;
      if (montado.current) {
        setEnCurso(null);
        if (conversacionId) tocarConversacion(conversacionId, tituloFinal);
      }
    }
  };

  // Abierta desde el Chat de LegalIA ("Verlo en Tu IA Normativa"): esa pregunta se envía sola en una conversación
  // nueva y se quita de la URL (al recargar no se repite)
  const preguntaEnUrl = parametros.get("pregunta");
  const preguntaEnviada = useRef(null);
  useEffect(() => {
    const pregunta = (preguntaEnUrl || "").trim().slice(0, 2000);
    if (!pregunta || preguntaEnviada.current === pregunta || cargando) return;
    preguntaEnviada.current = pregunta;
    setParametros({}, { replace: true });
    procesarPregunta(pregunta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preguntaEnUrl]);

  const enviar = (preguntaForzada) => {
    const pregunta = (typeof preguntaForzada === "string" ? preguntaForzada : input).trim();
    if (!pregunta || cargando || cargandoConversacion || bloqueado) return;
    setInput("");
    procesarPregunta(pregunta);
  };

  // Pregunta por voz: se transcribe en el back y se envía como una pregunta normal
  const enviarAudio = async (wav) => {
    if (cargando || bloqueado) return;
    setEnCurso({ texto: "", herramienta: null, transcribiendo: true });
    let transcripcion;
    try {
      ({ texto: transcripcion } = await transcribirAudio(wav));
    } catch (err) {
      if (montado.current) setEnCurso(null);
      anadirMensaje({ role: "assistant", content: textoError(t, err.codigo), estado: "error", local: true });
      return;
    }
    if (!transcripcion) {
      if (montado.current) setEnCurso(null);
      anadirMensaje({ role: "assistant", content: t("laeai.audio.noSpeech"), estado: "error", local: true });
      return;
    }
    await procesarPregunta(transcripcion, { audio: true });
  };

  // Último turno: se puede editar la pregunta o volver a generar la respuesta
  const ultimoUsuario = mensajes.map((m) => m.role).lastIndexOf("user");
  const [editando, setEditando] = useState(null); // texto de la pregunta en edición (null = sin editar)
  useEffect(() => setEditando(null), [activaId]);

  const rehacer = () => {
    const ultima = mensajes[ultimoUsuario];
    if (!ultima || cargando) return;
    procesarPregunta(ultima.content, { audio: ultima.audio, reemplazar: true });
  };

  const guardarEdicion = () => {
    const pregunta = (editando || "").trim();
    if (!pregunta || cargando) return;
    setEditando(null);
    // Si viene de un audio y se edita, pasa a ser una pregunta escrita
    const original = mensajes[ultimoUsuario];
    procesarPregunta(pregunta, { audio: original?.audio && pregunta === original.content, reemplazar: true });
  };

  const errorMicrofono = (codigo) => {
    const claves = { MIC_DENEGADO: "laeai.audio.micDenied", MIC_NO_DISPONIBLE: "laeai.audio.micUnavailable", SIN_VOZ: "laeai.audio.noSpeech" };
    anadirMensaje({ role: "assistant", content: t(claves[codigo] || "laeai.errors.generic"), estado: "error", local: true });
  };

  const detener = () => controlador.current?.abort();

  // Historial ---------------------------------------------------------------------------------------

  const seleccionar = (id) => {
    setHistorialMovil(false);
    if (cargando || id === activaId) return;
    setMensajes([]);
    setParametros({ c: String(id) });
  };

  const nuevoChat = () => {
    setHistorialMovil(false);
    if (cargando) return;
    setActivaId(null);
    setMensajes([]);
    setInput("");
    setParametros({});
  };

  const renombrar = async (id, titulo) => {
    try {
      const actualizada = await renombrarConversacion(id, titulo);
      setConversaciones((lista) => lista.map((c) => (c.id === id ? { ...c, titulo: actualizada.titulo } : c)));
      return true;
    } catch {
      toast.error(t("laeai.history.renameError"));
      return false;
    }
  };

  const eliminar = async (conversacion) => {
    const { isConfirmed } = await Swal.fire({
      title: t("laeai.history.deleteTitle"),
      html: `<b>${conversacion.titulo.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c]))}</b><br/>${t("laeai.history.deleteText")}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#888",
      cancelButtonText: t("laeai.cancel"),
      confirmButtonText: t("laeai.confirmDelete"),
    });
    if (!isConfirmed) return;
    try {
      await eliminarConversacion(conversacion.id);
      setConversaciones((lista) => lista.filter((c) => c.id !== conversacion.id));
      if (conversacion.id === activaId) nuevoChat();
    } catch {
      toast.error(t("laeai.history.deleteError"));
    }
  };

  const panelHistorial = (enCajon) => (
    <LAEAIHistorial
      conversaciones={conversaciones}
      activaId={activaId}
      cargando={cargandoLista}
      error={errorLista}
      bloqueado={cargando}
      onSeleccionar={seleccionar}
      onNueva={nuevoChat}
      onRenombrar={renombrar}
      onEliminar={eliminar}
      onCerrar={enCajon ? () => setHistorialMovil(false) : undefined}
      t={t}
    />
  );

  const estadoCarga = enCurso?.transcribiendo
    ? t("laeai.audio.transcribing")
    : enCurso?.herramienta
    ? t(`laeai.tools.${enCurso.herramienta}`, { defaultValue: Array.isArray(frasesCarga) ? frasesCarga[fraseIdx] : "" })
    : Array.isArray(frasesCarga) ? frasesCarga[fraseIdx] : "";

  return (
    <div className="flex h-full bg-white font-sans">
      {/* Historial (escritorio) */}
      <aside className="hidden md:flex w-[290px] shrink-0 flex-col" style={{ borderRight: "1px solid #e5e7eb" }}>
        {panelHistorial(false)}
      </aside>

      {/* Historial (móvil) */}
      {historialMovil && (
        <>
          <div className="md:hidden fixed inset-0 bg-black/40" style={{ zIndex: 1190 }} onClick={() => setHistorialMovil(false)} />
          {/* Por encima de la barra superior de la app */}
          <aside className="md:hidden fixed left-0 top-0 flex h-full w-[290px] flex-col bg-white shadow-xl" style={{ zIndex: 1200 }}>
            {panelHistorial(true)}
          </aside>
        </>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Cabecera */}
        <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3" style={{ borderBottom: "1px solid #e5e7eb" }}>
          <div className="flex min-w-0 items-center gap-2">
            <Sparkles size={22} className="shrink-0 text-gray-700" />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="m-0 text-base font-bold leading-tight text-gray-900">{t("laeai.title")}</h1>
              </div>
              <p className="m-0 truncate text-xs text-gray-500">{t("laeai.subtitle")}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 md:hidden">
            <button
              type="button"
              onClick={() => setHistorialMovil(true)}
              className="flex cursor-pointer items-center gap-1.5 rounded-lg border-none bg-transparent px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
            >
              <MessageCircleMore size={18} /> {t("laeai.history.show")}
            </button>
            <button
              type="button"
              onClick={nuevoChat}
              disabled={cargando}
              title={t("laeai.history.newChat")}
              aria-label={t("laeai.history.newChat")}
              className="flex cursor-pointer items-center rounded-lg border-none bg-transparent p-1.5 text-gray-600 hover:bg-gray-100"
            >
              <PenSquare size={18} />
            </button>
          </div>
        </div>

        {/* Mensajes */}
        <div ref={contenedorRef} onScroll={alHacerScroll} className={`min-h-0 flex-1 px-4 sm:px-8 ${vacia ? "overflow-hidden" : "overflow-y-auto"}`}>
          {cargandoConversacion ? (
            <div className="mx-auto max-w-3xl space-y-4 py-6">
              {[0, 1, 2].map((i) => (
                <div key={i} className={`h-12 animate-pulse rounded-2xl bg-gray-100 ${i % 2 ? "w-2/3" : "ml-auto w-1/2"}`} />
              ))}
            </div>
          ) : vacia ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 shadow-sm" style={{ border: "2px solid #93bbf0" }}>
                <Sparkles size={26} className="text-blue-500" />
              </div>
              <h2 className="mb-1 text-xl font-semibold text-gray-900">{t("laeai.greeting", { name: nombre })}</h2>
              <p className="max-w-md text-sm text-gray-700">{t("laeai.helpQuestion")}</p>
              {sugerencias.length > 0 && (
                <div className="mt-8 w-full max-w-2xl px-4">
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-700">{t("laeai.suggestedTitle")}</p>
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    {sugerencias.map((q) => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => enviar(q)}
                        className="cursor-pointer rounded-full border border-gray-200 bg-white px-4 py-2 text-left text-sm text-gray-700 transition-all hover:border-gray-300 hover:bg-gray-50 hover:shadow-sm"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="mx-auto max-w-3xl space-y-6 py-6">
              {mensajes.map((msg, i) => {
                const esError = msg.role === "assistant" && msg.estado === "error";
                const esParcial = msg.role === "assistant" && msg.estado === "parcial";
                const esUltimaPregunta = i === ultimoUsuario && !cargando && !bloqueado;
                // Volver a generar: bajo la última respuesta del último turno (no bajo avisos locales del micrófono)
                const puedeRehacer = !cargando && !bloqueado && ultimoUsuario >= 0 && i === mensajes.length - 1 && i > ultimoUsuario && !msg.local;
                // Pulgares: solo en respuestas guardadas en el servidor (con id) y que no sean un error
                const puedeVotar = msg.role === "assistant" && !!msg.id && !esError && !msg.local;
                if (esUltimaPregunta && editando !== null) {
                  return (
                    <div key={msg.id || `local-${i}`} className="flex justify-end">
                      <div className="w-full max-w-[80%] rounded-2xl bg-gray-100 p-3">
                        <textarea
                          value={editando}
                          maxLength={2000}
                          autoFocus
                          rows={Math.min(6, Math.max(2, editando.split("\n").length))}
                          onChange={(e) => setEditando(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); guardarEdicion(); }
                            if (e.key === "Escape") setEditando(null);
                          }}
                          aria-label={t("laeai.edit")}
                          className="w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-800"
                        />
                        <div className="mt-2 flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setEditando(null)}
                            className="cursor-pointer rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            {t("laeai.cancel")}
                          </button>
                          <button
                            type="button"
                            onClick={guardarEdicion}
                            disabled={!editando.trim()}
                            className="cursor-pointer rounded-lg border-none bg-gray-800 px-3 py-1.5 text-sm text-white hover:bg-gray-700 disabled:cursor-default disabled:bg-gray-300"
                          >
                            {t("laeai.editSend")}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={msg.id || `local-${i}`} className={`laeai-turno flex ${msg.role === "user" ? "justify-end" : "justify-start gap-3"}`}>
                    {msg.role === "assistant" && (
                      <div className="mt-1 shrink-0">
                        <Sparkles size={18} className={esError ? "text-red-300" : "text-gray-400"} />
                      </div>
                    )}
                    <div className={msg.role === "user" ? "max-w-[80%]" : "min-w-0 max-w-[85%]"}>
                      <div
                        className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                          msg.role === "user"
                            ? "bg-gray-100 text-gray-900"
                            : esError
                            ? "bg-red-50 text-red-700"
                            : "markdown-body laeai-respuesta text-gray-800"
                        }`}
                      >
                        {msg.role === "assistant" && !esError ? (
                          <>
                            <LAEAIRespuesta texto={msg.content} />
                            <LAEAITarjetas tarjetas={msg.tarjetas} t={t} />
                            {esParcial && (
                              <p className="mb-0 mt-2 text-xs italic text-gray-500">
                                {msg.error === "ASISTENTE_CANCELADO" ? t("laeai.stopped") : t("laeai.interrupted")}
                              </p>
                            )}
                            {msg.derivacion && (
                              <div className="mt-3">
                                <LAEAIDerivacion
                                  derivacion={msg.derivacion}
                                  conversacionId={activaId}
                                  mensajeId={msg.id}
                                  onActualizar={(d) => setMensajes((lista) => lista.map((m, j) => (j === i ? { ...m, derivacion: d } : m)))}
                                  user={user}
                                  t={t}
                                />
                              </div>
                            )}
                          </>
                        ) : esError ? (
                          <p className="m-0">{msg.local || msg.content ? msg.content : textoError(t, msg.error)}</p>
                        ) : (
                          msg.content.split("\n").map((linea, j) => (
                            <p key={j} className="mb-1 last:mb-0">
                              {msg.audio && j === 0 && (
                                <Mic size={13} className="mr-1.5 inline-block align-[-2px] text-gray-500" aria-label={t("laeai.audio.fromAudio")} />
                              )}
                              {linea}
                            </p>
                          ))
                        )}
                      </div>
                      {esUltimaPregunta && (
                        <div className="laeai-acciones mt-1 flex justify-end">
                          <button
                            type="button"
                            onClick={() => setEditando(msg.content)}
                            title={t("laeai.edit")}
                            aria-label={t("laeai.edit")}
                            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                          >
                            <Pencil size={14} />
                          </button>
                        </div>
                      )}
                      {(puedeVotar || puedeRehacer) && (
                        <div className="mt-1 flex items-center gap-0.5">
                          {puedeVotar && [true, false].map((util) => {
                            const Icono = util ? ThumbsUp : ThumbsDown;
                            const etiqueta = t(util ? "laeai.feedback.useful" : "laeai.feedback.notUseful");
                            const marcado = msg.util === util;
                            return (
                              <button
                                key={String(util)}
                                type="button"
                                onClick={() => votar(msg.id, util)}
                                title={etiqueta}
                                aria-label={etiqueta}
                                aria-pressed={marcado}
                                className={`flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border-none bg-transparent hover:bg-gray-100 ${
                                  marcado ? (util ? "text-green-600" : "text-red-600") : "text-gray-500 hover:text-gray-900"
                                }`}
                              >
                                <Icono size={14} fill="none" />
                              </button>
                            );
                          })}
                          {puedeVotar && (
                            <button
                              type="button"
                              onClick={() => copiar(msg.content)}
                              title={t("laeai.copy")}
                              aria-label={t("laeai.copy")}
                              className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                            >
                              <Copy size={14} />
                            </button>
                          )}
                          {puedeRehacer && (
                            <button
                              type="button"
                              onClick={rehacer}
                              title={t("laeai.regenerate")}
                              aria-label={t("laeai.regenerate")}
                              className="flex cursor-pointer items-center gap-1.5 rounded-md border-none bg-transparent px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                            >
                              <RotateCcw size={14} /> 
                              {/* {t("laeai.regenerate")} */}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Respuesta llegando */}
              {cargando && (
                enCurso.texto ? (
                  <div className="flex justify-start gap-3">
                    <div className="mt-1 shrink-0">
                      <Sparkles size={18} className="text-gray-400" />
                    </div>
                    <div className="min-w-0 max-w-[85%]">
                      <div className="markdown-body laeai-respuesta rounded-2xl px-4 py-3 text-sm leading-relaxed text-gray-800">
                        <LAEAIRespuesta texto={enCurso.texto} escribiendo />
                        <LAEAITarjetas tarjetas={enCurso.tarjetas} t={t} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Sparkles size={18} className="animate-pulse text-gray-400" />
                    <span className="animate-pulse text-sm text-gray-700">{estadoCarga}</span>
                  </div>
                )
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Entrada (con el límite diario agotado, la cuenta atrás en su lugar) */}
        <div className="px-4 pb-6 pt-2 sm:px-8 sm:pb-3">
          <LAEAILimite limite={limite} conChatElite={puedeUsarWidgetChatElite(user)} onReinicio={cargarLimite} t={t} />
          {!bloqueado && (
          <div
            className="relative mx-auto max-w-3xl rounded-xl bg-white transition-colors duration-150"
            style={{ border: inputFocused ? "2px solid #1a1a1a" : "1px solid #888" }}
          >
            <textarea
              value={input}
              maxLength={2000}
              onChange={(e) => {
                setInput(e.target.value);
                e.target.style.height = "auto";
                e.target.style.height = Math.min(e.target.scrollHeight, 150) + "px";
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  enviar();
                }
              }}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              placeholder={t("laeai.placeholder")}
              rows={1}
              className="w-full resize-none border-none bg-transparent px-4 py-3 text-sm text-gray-900 outline-none placeholder:text-gray-700"
              style={{ minHeight: "44px", maxHeight: "150px" }}
            />
            <div className="flex items-center justify-end px-2 pb-2">
              {cargando ? (
                <button
                  type="button"
                  onClick={detener}
                  title={t("laeai.stop")}
                  aria-label={t("laeai.stop")}
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border-none bg-gray-800 text-white transition-colors hover:bg-gray-700"
                >
                  <Square size={12} fill="currentColor" />
                </button>
              ) : !input.trim() && audioSoportado ? (
                // Sin texto escrito: botón de audio (mantener para grabar)
                <LAEAIGrabador deshabilitado={cargando || cargandoConversacion} onAudio={enviarAudio} onError={errorMicrofono} t={t} />
              ) : (
                <button
                  type="button"
                  onClick={() => enviar()}
                  disabled={!input.trim()}
                  className={`flex h-8 w-8 items-center justify-center rounded-lg border-none transition-colors ${
                    input.trim()
                      ? "cursor-pointer bg-gray-800 text-white hover:bg-gray-700"
                      : "cursor-default bg-transparent text-gray-300"
                  }`}
                >
                  <SendHorizontal size={16} />
                </button>
              )}
            </div>
          </div>
          )}
          <p className="mx-auto mb-0 mt-2 max-w-3xl text-center text-[11px] text-gray-500">{t("laeai.disclaimer")}</p>
        </div>
      </div>
    </div>
  );
}

export default LAEAI;
